/**
 * Channels service (M24, ADR-0046): human-agent desk, tenant channel settings, website widgets
 * and outbound consents. Contracts follow platform/services/channels (api.py, models.py).
 */
import type { Http } from "./http";
import type { WidgetReadiness } from "./visitors";

const enc = encodeURIComponent;

export type ChannelKind = "web" | "api" | "widget" | "email" | "chat" | "voice" | "mcp";
export type ChannelLang = "ar" | "en";

/** `SessionOut`. */
export interface ChannelSession {
  id: string;
  channel: ChannelKind;
  channels: string[];
  participant_kind: "user" | "visitor" | "external";
  language: ChannelLang;
  direction: "rtl" | "ltr";
  agent: string;
  state: "active" | "handover" | "closed";
  run_id: string | null;
  case_id: string | null;
  handover_task_id: string | null;
  created_at: string;
  updated_at: string;
}

/** `MessageOut`. */
export interface ChannelMessage {
  id: string;
  seq: number;
  direction: "inbound" | "outbound";
  sender: "participant" | "agent" | "human_agent" | "system";
  channel: ChannelKind;
  text: string;
  language: ChannelLang | null;
  direction_hint: "rtl" | "ltr";
  untrusted: boolean;
  attachments: unknown[];
  created_at: string;
}

/** `SettingsIO` (GET/PUT /v1/settings). */
export interface ChannelSettings {
  timezone: string;
  quiet_start: string;
  quiet_end: string;
  weekend_days: number[];
  default_language: ChannelLang;
  digits: "latin" | "arabic-indic";
  outbound_per_hour: number;
  outbound_per_recipient_day: number;
  opt_out_keywords: string[];
  human_queue_roles: string[];
  handover_sla_seconds: number;
  default_agent: string | null;
  /** Tenant consent purpose catalogue (plain labels); defaults until a tenant saves its own. */
  purposes?: ConsentPurpose[];
  /** Time zone and weekend follow the organisation settings (ADR-0064). */
  use_org_time?: boolean;
}

export interface ConsentPurpose {
  key: string;
  label: { ar: string; en: string };
}

export type WidgetPosition = "bottom-end" | "bottom-start";

export interface WidgetIn {
  name: string;
  allowed_origins: string[];
  agent: string;
  default_language: ChannelLang;
  theme: { accent?: string; position?: WidgetPosition };
  /** Microphone button in the website chat (ADR-0070). */
  voice?: boolean;
}

export type WidgetPatch = Partial<WidgetIn> & { status?: "active" | "disabled" };

/**
 * `WidgetOut`. The embed key is public by design (it sits in the website's HTML and only works
 * from the allowed origins), so it is listed again; rotating it revokes every old copy.
 */
export interface WidgetOut {
  id: string;
  name: string;
  allowed_origins: string[];
  agent: string;
  default_language: string;
  theme?: { accent?: string; position?: WidgetPosition };
  status?: "active" | "disabled";
  embed_key: string;
  key_version: number;
  voice?: boolean;
  created_at?: string;
  updated_at?: string | null;
}

/** Voice set-up of one agent (`/v1/voice/settings/{agent}`, ADR-0070). */
export interface VoiceSettingsIn {
  enabled: boolean;
  languages: ChannelLang[];
  voices: Partial<Record<ChannelLang, string | null>>;
  /** 0.5 (slower) – 2 (faster); 1 is normal. */
  speed: number;
  read_aloud: boolean;
  /** How the short spoken version is made when the agent gives none. */
  summary: "first_sentences" | "model";
  summary_sentences: number;
  /** Private conversations: listen and speak only with models on our own servers. */
  private: boolean;
  /** Words to recognise (names, places), comma separated. */
  hints: string;
}
export interface VoiceSettingsOut extends VoiceSettingsIn {
  agent: string;
  updated_at: string | null;
  updated_by: string | null;
}
export const VOICE_DEFAULTS: VoiceSettingsIn = {
  enabled: false,
  languages: ["ar", "en"],
  voices: {},
  speed: 1,
  read_aloud: true,
  summary: "first_sentences",
  summary_sentences: 2,
  private: false,
  hints: "",
};

export type ConnectorVendor = "whatsapp" | "teams" | "slack" | "telegram" | "mattermost" | "other";

/** `ConnectorIn`: secrets are values written to OpenBao server-side and never returned. */
export interface ChatConnectorIn {
  name: string;
  kind: "generic" | "mattermost";
  vendor: ConnectorVendor;
  display_name?: string | null;
  agent: string;
  mcp_endpoint?: string | null;
  secret?: string;
  outbound_url?: string;
  status?: "active" | "disabled";
}

/** `ConnectorOut`: whether secrets are set, never their values. */
export interface ChatConnector {
  name: string;
  kind: "generic" | "mattermost";
  vendor: ConnectorVendor;
  display_name: string | null;
  agent: string;
  mcp_endpoint: string | null;
  status: "active" | "disabled";
  has_secret: boolean;
  has_outbound: boolean;
  inbound_path: string;
  created_at: string;
  updated_at: string | null;
}

export type ConnectorTestReason = "no_outbound" | "conversation_required" | "not_found" | "disabled" | "secret_missing" | "upstream_failed";
export interface ConnectorTestOut {
  delivered: boolean;
  reason: ConnectorTestReason | null;
}

export interface EmailConnector {
  configured: boolean;
  inbound_path: string;
}

/** `TemplateOut`: outbound message templates and their fill-in fields. */
export interface OutboundTemplate {
  name: string;
  params: string[];
  text: { ar: string; en: string };
}

export interface OutboundIn {
  channel: ChannelKind;
  recipient: { address: string; connector?: string | null };
  purpose: string;
  template: string;
  params: Record<string, string>;
  language?: ChannelLang;
  idempotency_key: string;
}

/** `OutboundOut`: sent, deferred (quiet hours, rate limit) or blocked, with the reasons. */
export interface OutboundOut {
  id: string;
  status: "sent" | "deferred" | "blocked";
  reasons: string[];
  not_before: string | null;
  session_id: string | null;
}

export interface ChannelConsentIn {
  channel: ChannelKind;
  address: string;
  purpose: string;
  evidence: string;
}

/** `ConsentOut`: the recipient is stored as a hash only. */
export interface ChannelConsent {
  id: string;
  channel: string;
  purpose: string;
  recipient_sha256: string;
  granted_at: string;
  granted_by: string;
  revoked_at: string | null;
}

/** An API key for programs and AI apps (ADR-0073); the secret is only in `ApiKeyCreated.key`, once. */
export interface ApiKeyOut {
  id: string;
  name: string;
  agents: string[];
  hint: string;
  status: "active" | "revoked" | "expired";
  expires_at: string | null;
  created_at: string;
  created_by: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface ApiKeyCreated extends ApiKeyOut {
  key: string;
}

export interface ApiKeyIn {
  name: string;
  agents: string[];
  expires_in_days: number | null;
}

export function channelsApi(http: Http) {
  return {
    desk: (limit = 50) => http.get<ChannelSession[]>("channels", "/v1/desk/sessions", { limit }),
    session: (sid: string) => http.get<ChannelSession>("channels", `/v1/sessions/${enc(sid)}`),
    messages: (sid: string, after = 0) => http.get<ChannelMessage[]>("channels", `/v1/sessions/${enc(sid)}/messages`, { after, limit: 500 }),
    reply: (sid: string, text: string, clientMessageId: string) =>
      http.post<ChannelMessage>("channels", `/v1/sessions/${enc(sid)}/agent-reply`, { text, client_message_id: clientMessageId }),
    resume: (sid: string) => http.post<ChannelSession>("channels", `/v1/sessions/${enc(sid)}/resume`),
    close: (sid: string) => http.post<ChannelSession>("channels", `/v1/sessions/${enc(sid)}/close`),
    settings: () => http.get<ChannelSettings>("channels", "/v1/settings"),
    putSettings: (body: ChannelSettings) => http.put<ChannelSettings>("channels", "/v1/settings", body),
    createWidget: (body: WidgetIn) => http.post<WidgetOut>("channels", "/v1/widgets", body),
    widgets: () => http.get<WidgetOut[]>("channels", "/v1/widgets"),
    updateWidget: (id: string, body: WidgetPatch) => http.patch<WidgetOut>("channels", `/v1/widgets/${enc(id)}`, body),
    rotateWidgetKey: (id: string) => http.post<WidgetOut>("channels", `/v1/widgets/${enc(id)}/rotate-key`),
    /** ADR-0101: can visitors of each widget get an answer now, and what is missing. */
    widgetReadiness: () => http.get<WidgetReadiness[]>("channels", "/v1/widgets/readiness"),
    apiKeys: () => http.get<ApiKeyOut[]>("channels", "/v1/api-keys"),
    createApiKey: (body: ApiKeyIn) => http.post<ApiKeyCreated>("channels", "/v1/api-keys", body),
    revokeApiKey: (id: string) => http.post<ApiKeyOut>("channels", `/v1/api-keys/${enc(id)}/revoke`),
    voiceSettings: () => http.get<VoiceSettingsOut[]>("channels", "/v1/voice/settings"),
    /** One agent's voice set-up; voice off (`enabled: false`) when never set up (older services: 404 → `null`). */
    agentVoice: async (agent: string): Promise<VoiceSettingsOut | null> => {
      try {
        return await http.get<VoiceSettingsOut>("channels", `/v1/voice/settings/${enc(agent)}`);
      } catch (err) {
        if ((err as { status?: number }).status === 404) return null;
        throw err;
      }
    },
    putVoiceSettings: (agent: string, body: VoiceSettingsIn) => http.put<VoiceSettingsOut>("channels", `/v1/voice/settings/${enc(agent)}`, body),
    removeVoiceSettings: (agent: string) => http.delete<void>("channels", `/v1/voice/settings/${enc(agent)}`),
    connectors: () => http.get<ChatConnector[]>("channels", "/v1/chat-connectors"),
    putConnector: (body: ChatConnectorIn) => http.put<void>("channels", `/v1/chat-connectors/${enc(body.name)}`, body),
    testConnector: (name: string, body: { conversation_id?: string; language: ChannelLang }) =>
      http.post<ConnectorTestOut>("channels", `/v1/chat-connectors/${enc(name)}/test`, body),
    emailConnector: () => http.get<EmailConnector>("channels", "/v1/email-connector"),
    putEmailConnector: (webhookSecret: string) => http.put<EmailConnector>("channels", "/v1/email-connector", { webhook_secret: webhookSecret }),
    templates: () => http.get<OutboundTemplate[]>("channels", "/v1/outbound/templates"),
    outbound: (body: OutboundIn) => http.post<OutboundOut>("channels", "/v1/outbound", body),
    consents: (channel: ChannelKind, address: string) => http.get<ChannelConsent[]>("channels", "/v1/consents", { channel, address }),
    grantConsent: (body: ChannelConsentIn) => http.post<ChannelConsent>("channels", "/v1/consents", body),
    revokeConsent: (cid: string, reason: string) => http.post<ChannelConsent>("channels", `/v1/consents/${enc(cid)}/revoke`, { reason }),
  };
}

export type ChannelsApi = ReturnType<typeof channelsApi>;

/** The website snippet for a widget (served from the platform origin only, no CDN). */
export function widgetSnippet(origin: string, embedKey: string, lang: ChannelLang): string {
  return `<script src="${origin}/api/channels/widget/v1/ap-widget.js" data-embed-key="${embedKey}" data-lang="${lang}" defer></script>`;
}

/** Where the widget bundle is served (the platform origin; no CDN). */
export function widgetScriptUrl(origin: string): string {
  return `${origin}/api/channels/widget/v1/ap-widget.js`;
}

/**
 * The HTML of a sandboxed preview frame: the real widget bundle in preview mode (sample
 * messages, no network calls). Attribute values are escaped; the accent is validated.
 */
export function widgetPreviewDoc(origin: string, o: { lang: ChannelLang; accent: string; position: WidgetPosition; title?: string }): string {
  const esc = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  const accent = /^#[0-9a-fA-F]{6}$/.test(o.accent) ? o.accent : "#0b5394";
  const dir = o.lang === "ar" ? "rtl" : "ltr";
  const title = o.title ? ` data-title="${esc(o.title)}"` : "";
  return `<!doctype html><html lang="${o.lang}" dir="${dir}"><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;background:#f4f6f8;font-family:system-ui,sans-serif}</style></head><body><script src="${esc(widgetScriptUrl(origin))}" data-preview="true" data-lang="${o.lang}" data-accent="${accent}" data-position="${o.position}"${title}></script></body></html>`;
}

/**
 * "Add my website": turns what people type (`www.acme.com/contact`, `HTTPS://Shop.example:8443/`)
 * into the origin the widget checks (`https://www.acme.com`). Returns null when it is not a
 * website address.
 */
export function normaliseOrigin(input: string): string | null {
  let v = input.trim();
  if (!v) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) v = `https://${v}`;
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  if (!/^[A-Za-z0-9.-]+$/.test(u.hostname) || !u.hostname.includes(".") && u.hostname !== "localhost") return null;
  return `${u.protocol}//${u.host}`.toLowerCase();
}

/** Shows enough of an address to recognise it, hides the rest (`ah•••@acme.example`, `•••• 4567`). */
export function maskAddress(address: string): string {
  const a = address.trim();
  const at = a.indexOf("@");
  if (at > 0) {
    const name = a.slice(0, at);
    return `${name.slice(0, Math.min(2, name.length))}•••@${a.slice(at + 1)}`;
  }
  const digits = a.replace(/\D/g, "");
  if (digits.length >= 6) return `•••• ${digits.slice(-4)}`;
  return a.length <= 2 ? "••" : `${a.slice(0, 1)}•••${a.slice(-1)}`;
}
